import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { User } from 'firebase/auth';
import { Link, useNavigate } from 'react-router';

import { Button } from '@/components/Button/Button';

import { createApiClient } from '@/api/client';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { createProfileService } from '@/features/profile/profileService';
import {
  confirmJoinedCourse,
  CourseNotReadyError,
} from '@/features/staffInvites/staffInviteHandoff';
import { createStaffInviteService } from '@/features/staffInvites/staffInviteService';
import {
  staffInviteFullName,
  staffRoleDescription,
  type StaffInviteView,
} from '@/features/staffInvites/staffInviteTypes';
import { useThemeRouteOverride } from '@/theme/useTheme';
import kuduLogoUrl from '@/images/kudu-logo-full.svg';

import '@/features/marketing/marketing-tokens.css';

import styles from './LoginPage.module.css';
import inviteStyles from './StaffInvitePage.module.css';

export const STAFF_INVITE_KEY_STORAGE = 'kudu-staff-invite-key';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// Takes the invite key from the URL fragment (the link is /invite#<key>),
// keeps it for this tab only so it survives the sign-in hand-off, and removes
// it from the address bar. Runs before the page makes any request.
export function takeStaffInviteKey(): string | null {
  const fromHash = window.location.hash.replace(/^#/, '').trim();

  if (fromHash) {
    try {
      window.sessionStorage.setItem(STAFF_INVITE_KEY_STORAGE, fromHash);
    } catch {
      // Private mode: the key still works for this page load.
    }
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`,
    );
    return fromHash;
  }

  try {
    return window.sessionStorage.getItem(STAFF_INVITE_KEY_STORAGE);
  } catch {
    return null;
  }
}

function forgetStaffInviteKey() {
  try {
    window.sessionStorage.removeItem(STAFF_INVITE_KEY_STORAGE);
  } catch {
    // Nothing to forget.
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message.trim()
    ? error.message
    : 'Something went wrong. Please try again.';
}

function createAccountErrorMessage(error: unknown): string | 'in-use' {
  const code =
    error && typeof error === 'object' && 'code' in error ? String(error.code) : '';

  if (code === 'auth/email-already-in-use') {
    return 'in-use';
  }
  if (code === 'auth/weak-password') {
    return 'Choose a stronger password: at least 6 characters.';
  }
  return 'Your account couldn’t be created right now. Please try again.';
}

function sameEmail(a: string | null | undefined, b: string | null | undefined) {
  return Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());
}

function signInLink(email: string) {
  return `/login?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent('/invite')}`;
}

type Phase = 'idle' | 'creating' | 'accepting';

export function StaffInvitePage() {
  useThemeRouteOverride('light');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { createUserWithEmailAndPassword, loading, lookupEmailProviders, signOut, user } =
    useAuth();
  const [inviteKey, setInviteKey] = useState(takeStaffInviteKey);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [preferSignIn, setPreferSignIn] = useState(false);
  // Set when the invite was accepted but the course couldn't be confirmed on
  // the invitee's account: who to try again as.
  const [joinedAs, setJoinedAs] = useState<User | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Opening another invite link in this tab only changes the fragment, which
  // doesn't reload the page: take the new key and start over.
  useEffect(() => {
    function handleHashChange() {
      if (!window.location.hash) {
        return;
      }
      const nextKey = takeStaffInviteKey();
      if (nextKey) {
        setInviteKey(nextKey);
        setError(null);
        setPassword('');
        setPreferSignIn(false);
        setJoinedAs(null);
        setPhase('idle');
      }
    }
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const lookupService = useMemo(
    () => createStaffInviteService(createApiClient({ baseUrl: API_BASE_URL })),
    [],
  );

  const inviteQuery = useQuery<StaffInviteView>({
    enabled: Boolean(inviteKey),
    queryFn: () => lookupService.lookupInvite(inviteKey!),
    queryKey: ['staff-invite', inviteKey],
    retry: false,
  });
  const invite = inviteQuery.data;

  const providersQuery = useQuery<string[]>({
    enabled: Boolean(invite?.state === 'pending' && !loading && !user && lookupEmailProviders),
    queryFn: () => lookupEmailProviders!(invite!.email),
    queryKey: ['sign-in-methods', invite?.email],
    retry: false,
  });
  const hasAccount = preferSignIn || (providersQuery.data?.length ?? 0) > 0;

  async function acceptAs(authUser: User) {
    const userId = getKuduUserId(authUser);

    if (!inviteKey || !userId) {
      throw new Error('Sign in again to accept this invitation.');
    }

    const client = createApiClient({
      baseUrl: API_BASE_URL,
      getIdToken: (forceRefresh) => authUser.getIdToken(forceRefresh),
      getLegacyToken: async () => createLegacyKuduToken(authUser),
    });
    const courseId = await createStaffInviteService(client).acceptInvite({
      key: inviteKey,
      userId,
    });

    // The server has joined them. The course opens only once the app's own
    // copy of their profile shows it; the key is kept until then, so a reload
    // can still finish the job.
    await confirmJoinedCourse({
      courseId,
      profileService: createProfileService(client),
      queryClient,
      userId,
    });

    forgetStaffInviteKey();
    navigate(`/app/courses/${encodeURIComponent(courseId)}`, { replace: true });
  }

  async function join(authUser: User) {
    setError(null);
    setPhase('accepting');
    try {
      await acceptAs(authUser);
    } catch (acceptError) {
      setJoinedAs(acceptError instanceof CourseNotReadyError ? authUser : null);
      setError(errorMessage(acceptError));
      setPhase('idle');
    }
  }

  async function handleAccept() {
    if (user) {
      await join(user);
    }
  }

  async function handleOpenJoinedCourse() {
    if (joinedAs) {
      await join(joinedAs);
    }
  }

  async function handleCreateAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!invite || !createUserWithEmailAndPassword) {
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setError(null);
    setPhase('creating');
    let credentialUser: User;
    try {
      credentialUser = (await createUserWithEmailAndPassword(invite.email, password)).user;
    } catch (createError) {
      const message = createAccountErrorMessage(createError);
      if (message === 'in-use') {
        setPreferSignIn(true);
        setError('That email already has a Kudu account. Sign in to accept the invitation.');
      } else {
        setError(message);
      }
      setPhase('idle');
      return;
    }

    await join(credentialUser);
  }

  // Which state is showing: focus moves to its heading whenever it changes.
  // Work in progress and its outcome come first. Signing in clears the query
  // cache, which loads the invite again, and that must not put "Checking your
  // invitation…" back on screen halfway through joining.
  const stateKey = !inviteKey
    ? 'no-key'
    : phase !== 'idle'
      ? 'working'
      : joinedAs
        ? 'not-ready'
        : inviteQuery.isLoading || (invite?.state === 'pending' && loading)
          ? 'loading'
          : inviteQuery.isError
            ? 'lookup-error'
            : invite?.state === 'pending'
              ? user
                ? sameEmail(user.email, invite.email)
                  ? 'accept'
                  : 'wrong-account'
                : hasAccount
                  ? 'sign-in'
                  : 'create'
              : (invite?.state ?? 'loading');

  useEffect(() => {
    headingRef.current?.focus();
  }, [stateKey]);

  const heading = (text: string) => (
    <h1
      className={`${styles.title} ${inviteStyles.heading}`}
      id="staff-invite-title"
      ref={headingRef}
      tabIndex={-1}
    >
      {text}
    </h1>
  );

  const errorLine = error ? (
    <p className={styles.error} role="alert">
      {error}
    </p>
  ) : null;

  const inviter = invite?.inviterName || 'Someone';
  const summary =
    invite && invite.role ? (
      <p className={styles.description}>
        {inviter} invited you to join <strong>{invite.courseTitle || 'a course'}</strong> on
        Kudu as {staffRoleDescription(invite.role)}.
      </p>
    ) : null;

  let body: ReactNode;
  switch (stateKey) {
    case 'no-key':
      body = (
        <>
          {heading('Open your invitation')}
          <p className={styles.description}>
            Open the invitation link you were sent. It includes the code that
            lets you join the course.
          </p>
        </>
      );
      break;
    case 'loading':
      body = (
        <>
          {heading('Checking your invitation…')}
          <p aria-live="polite" className={styles.description}>
            One moment.
          </p>
        </>
      );
      break;
    case 'lookup-error':
      body = (
        <>
          {heading('We couldn’t check this invitation')}
          <p className={styles.description}>Check your connection and try again.</p>
          <Button onClick={() => void inviteQuery.refetch()} size="large" variant="primary">
            Try again
          </Button>
        </>
      );
      break;
    case 'working':
      body = (
        <>
          {heading(phase === 'creating' ? 'Creating your account…' : 'Joining the course…')}
          <p aria-live="polite" className={styles.description}>
            You&apos;ll be taken to the course in a moment.
          </p>
        </>
      );
      break;
    case 'not-ready':
      body = (
        <>
          {heading(`${invite?.courseTitle || 'The course'} isn’t ready to open yet`)}
          {errorLine}
          <p className={styles.description}>
            If it still won&apos;t open, ask{' '}
            {invite?.inviterName || 'the person who invited you'} for a new
            invitation link.
          </p>
          <Button onClick={() => void handleOpenJoinedCourse()} size="large" variant="primary">
            Open the course
          </Button>
        </>
      );
      break;
    case 'invalid':
      body = (
        <>
          {heading('This invitation link isn’t valid')}
          <p className={styles.description}>
            It may have been cancelled. Ask the person who invited you for a new link.
          </p>
        </>
      );
      break;
    case 'expired':
      body = (
        <>
          {heading('This invitation has expired')}
          <p className={styles.description}>
            Ask {invite?.inviterName || 'the person who invited you'} to renew it,
            then open the link again.
          </p>
        </>
      );
      break;
    case 'course_gone':
      body = (
        <>
          {heading('This course no longer exists')}
          <p className={styles.description}>
            The invitation can&apos;t be used because its course was deleted.
          </p>
        </>
      );
      break;
    case 'accepted':
      body = (
        <>
          {heading('This invitation has already been used')}
          <p className={styles.description}>
            Each invitation link works once.
            {user ? ' If you accepted it, you can open the course.' : ''}
          </p>
          {errorLine}
          {user ? (
            <Button onClick={() => void handleAccept()} size="large" variant="primary">
              Open the course
            </Button>
          ) : null}
        </>
      );
      break;
    case 'accept':
      body = (
        <>
          {heading(`Join ${invite?.courseTitle || 'the course'}`)}
          {summary}
          {errorLine}
          <Button
            className={styles.submitButton}
            fullWidth
            onClick={() => void handleAccept()}
            size="large"
            variant="primary"
          >
            Accept invitation
          </Button>
        </>
      );
      break;
    case 'wrong-account':
      body = (
        <>
          {heading(`This invitation is for ${invite?.email ?? 'someone else'}`)}
          <p className={styles.description}>
            You&apos;re signed in as {user?.email ?? 'another account'}. Sign out, then
            continue with {invite?.email}.
          </p>
          <Button onClick={() => void signOut()} size="large" variant="primary">
            Sign out and continue
          </Button>
        </>
      );
      break;
    case 'sign-in':
      body = (
        <>
          {heading(`Join ${invite?.courseTitle || 'the course'}`)}
          {summary}
          {errorLine}
          <p className={styles.description}>
            {invite?.email} already has a Kudu account. Sign in to accept the invitation.
          </p>
          <Button as={Link} fullWidth size="large" to={signInLink(invite?.email ?? '')} variant="primary">
            Sign in to accept
          </Button>
        </>
      );
      break;
    default:
      body = (
        <>
          {heading(`Join ${invite?.courseTitle || 'the course'}`)}
          {summary}
          <form className={styles.form} noValidate onSubmit={(event) => void handleCreateAccount(event)}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="invite-email">
                Email
              </label>
              <input
                autoComplete="email"
                className={styles.input}
                id="invite-email"
                readOnly
                type="email"
                value={invite?.email ?? ''}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="invite-password">
                Choose a password
              </label>
              <input
                aria-describedby={error ? 'invite-error' : 'invite-password-hint'}
                aria-invalid={Boolean(error)}
                autoComplete="new-password"
                className={styles.input}
                id="invite-password"
                minLength={6}
                onChange={(event) => setPassword(event.target.value)}
                required
                type="password"
                value={password}
              />
              <span className={inviteStyles.hint} id="invite-password-hint">
                At least 6 characters.
              </span>
            </div>
            {error ? (
              <p className={styles.error} id="invite-error" role="alert">
                {error}
              </p>
            ) : null}
            <Button className={styles.submitButton} fullWidth size="large" type="submit" variant="primary">
              Create account and join
            </Button>
          </form>
          <p className={styles.notice}>
            Your profile is set up as {invite ? staffInviteFullName(invite) : 'invited'} ({invite?.email}).
            Already have a Kudu account?{' '}
            <Link to={signInLink(invite?.email ?? '')}>Sign in</Link>
          </p>
        </>
      );
  }

  return (
    <main className={styles.page}>
      <div className={styles.content}>
        <Link className={styles.wordmark} to="/">
          <img alt="Kudu" className={styles.wordmarkLogo} src={kuduLogoUrl} />
        </Link>
        <section aria-labelledby="staff-invite-title" className={styles.card}>
          <div className={styles.cardHeader}>
            {body}
          </div>
        </section>
      </div>
    </main>
  );
}
